// GAVION booking: the PublicBookingApi of the demo build (plan D19). The same slot engine as the server, fake
// availability (every weekday 10:00–11:00 in the rep's zone, plus the rep's live demo bookings) and FakeRest rows in
// place of the SQL of 53 (booking_prepare, booking_confirm, booking_by_token, booking_rescheduled, booking_cancelled).
// It mirrors the server's answers: a second booking by the same address is `already_booked` (nothing moves, no token),
// a booking moves at most MAX_RESCHEDULES times (`reschedule_limit`), an unusable token is `link_expired`.
import type { RecordStore } from "../../../dialer/api/recordStore";
import { DEMO_USER } from "../../../demo/emptyDemoDb";
import { isCustomLeadStatus, leadStatusesFromConfigRows } from "../../../domain/leads/leadStatusModel";
import { normalizePhone } from "../../../domain/phone";
import {
  DEFAULT_HORIZON_DAYS,
  DEFAULT_NOTICE_HOURS,
  DEFAULT_TZ,
  DEFAULT_WEEKLY_HOURS,
  validWeeklyHours,
} from "../../../../../../supabase/functions/_shared/booking/settings.ts";
import {
  type BusyInterval,
  computeSlots,
  isBookable,
  SLOT_MINUTES,
  type SlotRules,
} from "../../../../../../supabase/functions/_shared/booking/slots.ts";
import {
  MANAGE_TOKEN_RE,
  newManageToken,
  sha256Hex,
} from "../../../../../../supabase/functions/_shared/booking/token.ts";
import { wallToUtc, zoneParts } from "../../../../../../supabase/functions/_shared/gavion/time/zone.ts";
import {
  type BookInput,
  type ManageView,
  type PublicBookingApi,
  PublicBookingError,
  type PublicRep,
  type SlotPage,
} from "./types";

type Row = Record<string, unknown> & { id: number };
const MIN = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;
/** The server's cap (gavion-booking handler MAX_RESCHEDULES). */
const MAX_RESCHEDULES = 5;
const EMAIL = /^[^@\s<>]+@[^@\s<>]+\.[^@\s<>]+$/;
const LIVE = ["booked", "rescheduled"];
const QUEUE = ["new", "in_progress", "callback", "cooling_down"];
const same = (a: unknown, b: unknown) => String(a) === String(b);
const text = (v: unknown) => (typeof v === "string" ? v : "");
/** The demo user's own link (the demo build: /demo/gavion-demo). Not on the reserved list, like any real slug. */
export const DEMO_BOOKING_SLUG = "gavion-demo";
const list = (v: unknown): Row[] => (Array.isArray(v) ? (v as Row[]) : []);

export function createFakePublicBookingApi(o: { store: RecordStore; now?: () => Date }): PublicBookingApi {
  const now = o.now ?? (() => new Date());
  const nowIso = () => now().toISOString();
  const rows = (r: string) => o.store.list<Row>(r);
  const defaultSlug = DEMO_BOOKING_SLUG;

  const rulesOf = (row: Row | undefined): SlotRules => {
    const hours = row?.weekly_hours;
    return {
      tz: text(row?.tz) || DEFAULT_TZ,
      weeklyHours: validWeeklyHours(hours) ? hours : DEFAULT_WEEKLY_HOURS,
      noticeHours: Number(row?.notice_hours ?? DEFAULT_NOTICE_HOURS),
      horizonDays: Number(row?.horizon_days ?? DEFAULT_HORIZON_DAYS),
      holidays: [],
    };
  };
  const sale = async (salesId: unknown) => (await rows("sales")).find((s) => same(s.id, salesId));
  const settingsOf = async (salesId: unknown) =>
    (await rows("booking_settings")).find((s) => same(s.sales_id ?? s.id, salesId));

  /** A saved, enabled link; else the demo user's default link while they have no row. */
  async function repOfSlug(slug: string): Promise<{ salesId: number; rules: SlotRules; rep: PublicRep } | null> {
    const all = await rows("booking_settings");
    const row = all.find((s) => s.slug === slug);
    let salesId: number | null = null;
    if (row) salesId = row.enabled === true ? Number(row.sales_id ?? row.id) : null;
    else if (slug === defaultSlug && !all.some((s) => same(s.sales_id ?? s.id, DEMO_USER.id))) salesId = DEMO_USER.id;
    if (salesId === null) return null;
    const s = await sale(salesId);
    if (!s || s.disabled === true) return null;
    return { salesId, rules: rulesOf(row), rep: { firstName: text(s.first_name), lastName: text(s.last_name), avatarUrl: null } };
  }

  async function busyFor(salesId: number, rules: SlotRules, from: number, to: number, ignore?: number): Promise<BusyInterval[]> {
    const out: BusyInterval[] = [];
    const a = zoneParts(new Date(from - DAY), rules.tz);
    const b = zoneParts(new Date(to + DAY), rules.tz);
    for (let d = Date.UTC(a.year, a.month - 1, a.day); d <= Date.UTC(b.year, b.month - 1, b.day); d += DAY) {
      const wd = new Date(d).getUTCDay();
      if (wd === 0 || wd === 6) continue;
      out.push({ start: wallToUtc(d + 600 * MIN, rules.tz).toISOString(), end: wallToUtc(d + 660 * MIN, rules.tz).toISOString() });
    }
    for (const bk of await rows("bookings"))
      if (same(bk.sales_id, salesId) && LIVE.includes(text(bk.status)) && bk.id !== ignore)
        out.push({ start: text(bk.starts_at), end: text(bk.ends_at) });
    return out;
  }

  const page = (rules: SlotRules, busy: BusyInterval[], from: number, to: number, exclude: string[], rep: PublicRep): SlotPage => {
    const horizonEnd = now().getTime() + rules.horizonDays * DAY;
    return {
      rep,
      slots: computeSlots({ rules, busy, now: now(), from: new Date(from), to: new Date(to), exclude }),
      horizonEnd: new Date(horizonEnd).toISOString(),
      nextFrom: to < horizonEnd ? new Date(to).toISOString() : null,
    };
  };

  async function bookingOfToken(token: string): Promise<Row | null> {
    if (!MANAGE_TOKEN_RE.test(token)) return null;
    const hash = await sha256Hex(token);
    const bookings = await rows("bookings");
    const direct = bookings.find((b) => b.manage_token_hash === hash);
    if (direct) return direct;
    const t = (await rows("booking_tokens")).find((x) => x.token_hash === hash);
    return t ? (bookings.find((b) => same(b.id, t.booking_id)) ?? null) : null;
  }
  async function viewOf(b: Row): Promise<ManageView> {
    const s = await sale(b.sales_id);
    return {
      status: text(b.status) as ManageView["status"],
      startsAt: text(b.starts_at),
      endsAt: text(b.ends_at),
      language: b.language === "en" ? "en" : "fr",
      rep: { firstName: text(s?.first_name), lastName: text(s?.last_name), email: text(s?.email) },
    };
  }
  async function usableBooking(token: string): Promise<Row> {
    const b = await bookingOfToken(token);
    if (!b) throw new PublicBookingError("link_expired");
    if (!LIVE.includes(text(b.status)) || Date.parse(text(b.starts_at)) - now().getTime() <= HOUR)
      throw new PublicBookingError("link_expired", { rep: (await viewOf(b)).rep });
    return b;
  }
  const note = (dealId: unknown, salesId: number, body: string) =>
    dealId == null
      ? Promise.resolve()
      : o.store.create("deal_notes", { deal_id: dealId, type: "booking", text: body, date: nowIso(), sales_id: salesId });

  async function leadFor(email: string, phone: string, salesId: number, i: BookInput): Promise<Row> {
    const companies = await rows("companies");
    const hits = new Set(
      companies.filter((c) => text(c.email).toLowerCase() === email || c.phone_e164 === phone).map((c) => String(c.id)),
    );
    for (const k of await rows("contacts")) {
      const byEmail = list(k.email_jsonb).some((e) => text(e.email).trim().toLowerCase() === email);
      const byPhone = list(k.phone_jsonb).some((p) => normalizePhone(p.number) === phone);
      if (k.company_id != null && (byEmail || byPhone)) hits.add(String(k.company_id));
    }
    const matched = companies
      .filter((c) => hits.has(String(c.id)))
      .sort(
        (a, b) =>
          Number(same(b.assigned_sales_id, salesId)) - Number(same(a.assigned_sales_id, salesId)) ||
          text(b.created_at).localeCompare(text(a.created_at)) ||
          b.id - a.id,
      );
    if (matched[0]) {
      const status = text(matched[0].prospect_status);
      // GAVION lead statuses: an added status moves to demo_booked too (booking_prepare)
      if (
        QUEUE.includes(status) ||
        isCustomLeadStatus(leadStatusesFromConfigRows(await rows("configuration")), status)
      )
        await o.store.update("companies", matched[0].id, { prospect_status: "demo_booked" });
      return matched[0];
    }
    return o.store.create<Row>("companies", {
      name: i.company.trim(), email, phone_number: phone, phone_e164: phone, assigned_sales_id: salesId, sales_id: salesId,
      prospect_status: "demo_booked", language: i.lang, source_query: "booking", timezone: DEFAULT_TZ, tags: [],
      created_at: nowIso(), updated_at: nowIso(),
    });
  }
  async function contactFor(companyId: number, email: string, phone: string, i: BookInput): Promise<number> {
    const k = (await rows("contacts")).find(
      (c) => same(c.company_id, companyId) && list(c.email_jsonb).some((e) => text(e.email).trim().toLowerCase() === email),
    );
    if (k) return k.id;
    const [first, ...rest] = i.name.trim().split(/\s+/);
    const c = await o.store.create<Row>("contacts", {
      first_name: first, last_name: rest.join(" "), company_id: companyId, sales_id: null,
      email_jsonb: [{ email, type: "Work" }], phone_jsonb: [{ number: phone, type: "Work" }], language: i.lang, tags: [],
      first_seen: nowIso(), last_seen: nowIso(),
    });
    return c.id;
  }
  /**
   * Plan D15: an online booking in the demo stands for a connected Outlook (its event exists), so the deal page does not ask to
   * reconnect Microsoft 365. Only the rep who has no Microsoft row yet; a row the rep set up (or disconnected) is never touched.
   */
  async function ensureMicrosoft(salesId: number): Promise<void> {
    const has = (await rows("integration_accounts")).some((a) => same(a.sales_id, salesId) && a.provider === "microsoft");
    if (has) return;
    await o.store.create("integration_accounts", {
      sales_id: salesId, provider: "microsoft", status: "connected",
      scopes: ["mail.readwrite", "mail.send", "calendars.readwrite", "onlinemeetings.readwrite"],
      external_email: null, last_sync_at: null, last_error: null,
    });
  }
  async function dealFor(company: Row, contactId: number, salesId: number, startsAt: string): Promise<number> {
    // the demo's day in the rep's zone, as the SQL booking_prepare sets it
    const tz = rulesOf(await settingsOf(salesId)).tz;
    const closing = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(startsAt));
    const demo = (id: number) => ({
      demo_at: startsAt, outlook_event_id: `demo-booking-${id}`,
      teams_join_url: `https://teams.microsoft.com/l/meetup-join/demo-booking-${id}`, updated_at: nowIso(),
    });
    const open = (await rows("deals"))
      .filter((d) => same(d.company_id, company.id) && !["gagne", "perdu"].includes(text(d.stage)) && !d.archived_at
        && (d.sales_id == null || same(d.sales_id, salesId)))
      .sort((a, b) => b.id - a.id)[0];
    if (open) {
      const ids = list(open.contact_ids as unknown).map(Number);
      await o.store.update("deals", open.id, {
        ...demo(open.id), expected_closing_date: open.expected_closing_date || closing, stage: open.stage === "nouveau_lead" ? "qualifie" : open.stage, sales_id: open.sales_id ?? salesId,
        contact_ids: ids.includes(contactId) ? ids : [...ids, contactId],
      });
      return open.id;
    }
    const d = await o.store.create<Row>("deals", {
      name: `${text(company.name)} — Démo`, company_id: company.id, contact_ids: [contactId], stage: "qualifie", amount: 0,
      sales_id: salesId, index: 0, source: "organic", category: null, expected_closing_date: closing, created_at: nowIso(),
    });
    await o.store.update("deals", d.id, demo(d.id));
    return d.id;
  }

  return {
    async slots(q) {
      const from = Date.parse(q.from);
      const to = Date.parse(q.to);
      if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) throw new PublicBookingError("invalid_query");
      if ("token" in q) {
        const b = await usableBooking(q.token);
        const salesId = Number(b.sales_id);
        const rules = rulesOf(await settingsOf(salesId));
        const s = await sale(salesId);
        const rep = { firstName: text(s?.first_name), lastName: text(s?.last_name), avatarUrl: null };
        return page(rules, await busyFor(salesId, rules, from, to, b.id), from, to, [text(b.starts_at)], rep);
      }
      const r = await repOfSlug(q.slug.trim().toLowerCase());
      if (!r) throw new PublicBookingError("not_found");
      return page(r.rules, await busyFor(r.salesId, r.rules, from, to), from, to, [], r.rep);
    },

    async book(i) {
      if (i.website.trim() !== "") return { status: "received", startsAt: null, endsAt: null, manageToken: null };
      const email = i.email.trim().toLowerCase();
      const phone = normalizePhone(i.phone);
      if (!EMAIL.test(email) || !i.name.trim() || !i.company.trim()) throw new PublicBookingError("invalid_body");
      if (!phone) throw new PublicBookingError("invalid_phone");
      const r = await repOfSlug(i.slug.trim().toLowerCase());
      if (!r) throw new PublicBookingError("not_found");
      const start = Date.parse(i.start);
      const t = now().getTime();
      const winFrom = Math.max(t, Math.floor((Number.isFinite(start) ? start : t) / DAY) * DAY);
      const winTo = winFrom + 7 * DAY;
      const busy = await busyFor(r.salesId, r.rules, winFrom, winTo);
      if (!Number.isFinite(start) || !isBookable(r.rules, busy, now(), new Date(start).toISOString()))
        throw new PublicBookingError("slot_taken", { slots: page(r.rules, busy, winFrom, winTo, [], r.rep).slots });
      const bookings = await rows("bookings");
      if (bookings.filter((b) => b.prospect_email === email && t - Date.parse(text(b.created_at)) < DAY).length >= 3)
        throw new PublicBookingError("rate_limited");
      // Preflight H1: an address with a future booking with this rep moves nothing here; the server emails the existing
      // manage link, the page says « déjà une démo prévue ».
      const old = bookings.find(
        (b) => same(b.sales_id, r.salesId) && b.prospect_email === email && LIVE.includes(text(b.status)) && Date.parse(text(b.starts_at)) > t,
      );
      if (old) throw new PublicBookingError("already_booked");
      const startsAt = new Date(start).toISOString();
      const endsAt = new Date(start + SLOT_MINUTES * MIN).toISOString();
      const { token, hash } = await newManageToken();
      const company = await leadFor(email, phone, r.salesId, i);
      const contactId = await contactFor(company.id, email, phone, i);
      const dealId = await dealFor(company, contactId, r.salesId, startsAt);
      await ensureMicrosoft(r.salesId);
      await o.store.create("bookings", {
        uid: crypto.randomUUID(), sales_id: r.salesId, company_id: company.id, contact_id: contactId, deal_id: dealId,
        starts_at: startsAt, ends_at: endsAt, status: "booked", manage_token_hash: hash, prospect_name: i.name.trim(),
        prospect_company: i.company.trim(), prospect_email: email, prospect_phone: phone, language: i.lang, ip_hash: null,
        reschedule_count: 0, created_at: nowIso(), updated_at: nowIso(),
      });
      await note(dealId, r.salesId, `Démo réservée en ligne : ${startsAt} — ${i.name.trim()}, ${email}, ${phone}`);
      await o.store.create("notifications", {
        sales_id: r.salesId, kind: "booking", title: `Nouvelle démo réservée : ${text(company.name)}, ${startsAt}`, body: null,
        link: `/companies/${company.id}/show`, read_at: null, created_at: nowIso(),
      });
      return { status: "booked", startsAt, endsAt, manageToken: token };
    },

    async manage(token) {
      return viewOf(await usableBooking(token));
    },

    async reschedule(token, start) {
      const b = await usableBooking(token);
      if (Number(b.reschedule_count ?? 0) >= MAX_RESCHEDULES) throw new PublicBookingError("reschedule_limit");
      const salesId = Number(b.sales_id);
      const rules = rulesOf(await settingsOf(salesId));
      const s = Date.parse(start);
      const t = now().getTime();
      const winFrom = Math.max(t, Math.floor((Number.isFinite(s) ? s : t) / DAY) * DAY);
      const winTo = winFrom + 7 * DAY;
      const busy = await busyFor(salesId, rules, winFrom, winTo, b.id);
      const startsAt = Number.isFinite(s) ? new Date(s).toISOString() : "";
      if (!startsAt || startsAt === b.starts_at || !isBookable(rules, busy, now(), startsAt))
        throw new PublicBookingError("slot_taken");
      const endsAt = new Date(s + SLOT_MINUTES * MIN).toISOString();
      const count = Number(b.reschedule_count ?? 0) + 1;
      await o.store.update("bookings", b.id, {
        status: "rescheduled", starts_at: startsAt, ends_at: endsAt, reschedule_count: count, updated_at: nowIso(),
      });
      if (b.deal_id != null) await o.store.update("deals", b.deal_id as number, { demo_at: startsAt, updated_at: nowIso() });
      await note(b.deal_id, salesId, `Démo déplacée en ligne par le prospect : ${startsAt}`);
      return viewOf({ ...b, status: "rescheduled", starts_at: startsAt, ends_at: endsAt });
    },

    async cancel(token) {
      const b = await usableBooking(token);
      await o.store.update("bookings", b.id, { status: "cancelled", updated_at: nowIso() });
      if (b.deal_id != null)
        await o.store.update("deals", b.deal_id as number, { demo_at: null, outlook_event_id: null, teams_join_url: null });
      await note(b.deal_id, Number(b.sales_id), "Démo annulée en ligne par le prospect. L'étape de l'affaire ne change pas.");
      return viewOf({ ...b, status: "cancelled" });
    },
  };
}
